import { useState } from 'react';
import { Badge, type BadgeVariant } from '../ui/badge';
import { Button } from '../ui/button';
import type { BotLaunchStatus, BotReplyMode } from '../../types/leadBot';
import type { ClubLaunchView } from '../../types/kyckConnect';
import { LAUNCH_HELP, LAUNCH_LABELS, REPLY_MODE_LABELS, type LaunchActor } from '../../lib/leadBot/launch';
import { confirmText, moveLabel, movesFor, reviewCount } from '../../lib/kyckConnect/launchView';

// LAUNCH STATUS, REPLY MODE AND COPIED-SETUP REVIEW: one presentational card, hosted by the staff Setup and the portal Setup.
// ⚠️ It decides nothing: the server refuses an illegal move, an un-checked go-live and a non-admin reply-mode change, and its refusal
// sentence (`error`) is shown here word for word. The buttons only follow `canTransition` so nobody is offered a move that cannot work.

const VARIANT: Record<BotLaunchStatus, BadgeVariant> = { setting_up: 'warning', testing: 'info', live: 'success', on_hold: 'neutral' };

export interface LaunchControlsProps {
  clubName: string;
  launch: ClubLaunchView;
  actor: LaunchActor;
  /** Whether this viewer may save at all (a portal user without setup access reads only). */
  canEdit: boolean;
  /** Reply mode may be changed only by a Kyck admin during the pilot; everyone else sees it. */
  canChangeReplyMode: boolean;
  busy: boolean;
  /** The server's last refusal or failure, in its own words. */
  error: string | null;
  onMove: (to: BotLaunchStatus, confirmed: boolean) => void;
  onReplyMode: (mode: BotReplyMode, confirmed: boolean) => void;
  onConfirmReviewed: (items: { sections?: string[]; promotionIds?: string[]; followUps?: boolean }) => void;
}

type Pending = { kind: 'move'; to: BotLaunchStatus } | { kind: 'mode'; mode: BotReplyMode };

export function LaunchControls(p: LaunchControlsProps) {
  const { launch } = p;
  const [pending, setPending] = useState<Pending | null>(null);
  const moves = p.canEdit ? movesFor(launch.status, p.actor) : [];
  const needAdminConfirm = p.actor === 'kyck_admin';
  const otherMode: BotReplyMode = launch.replyMode === 'auto' ? 'approve' : 'auto';
  const count = reviewCount(launch.review);
  const from = launch.copiedFromName;

  const confirmSentence = (x: Pending): string =>
    x.kind === 'move'
      ? confirmText(x.to)
      : `Switch to “${REPLY_MODE_LABELS[x.mode]}”? ${x.mode === 'auto' ? 'The assistant will text real leads on its own once the club is live.' : 'Real leads will get drafts a person approves.'}`;

  return (
    <div className="space-y-3 rounded-md border border-border bg-card p-4" data-testid="launch-controls">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={VARIANT[launch.status]} data-testid="launch-status">{LAUNCH_LABELS[launch.status]}</Badge>
        <span className="text-sm text-foreground">{LAUNCH_HELP[launch.status]}</span>
      </div>

      {launch.status === 'live' && (
        <p className="text-sm text-foreground" data-testid="reply-mode-line">
          Reply setting: <strong>{REPLY_MODE_LABELS[launch.replyMode]}</strong>.
          {p.canChangeReplyMode ? (
            <>
              {' '}
              <button type="button" className="underline" onClick={() => setPending({ kind: 'mode', mode: otherMode })}>
                Change to “{REPLY_MODE_LABELS[otherMode]}”
              </button>
            </>
          ) : (
            <span className="text-xs text-muted-foreground"> Only Kyck changes this for now.</span>
          )}
        </p>
      )}

      {moves.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="launch-moves">
          {moves.map((to) => (
            <Button
              key={to}
              size="sm"
              variant={to === 'live' ? 'default' : 'outline'}
              disabled={p.busy}
              onClick={() => (needAdminConfirm ? setPending({ kind: 'move', to }) : p.onMove(to, false))}
            >
              {moveLabel(to)}
            </Button>
          ))}
        </div>
      )}

      {pending && (
        <div role="alertdialog" className="rounded border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="launch-confirm">
          <p>{confirmSentence(pending)}</p>
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              disabled={p.busy}
              onClick={() => {
                if (pending.kind === 'move') p.onMove(pending.to, true);
                else p.onReplyMode(pending.mode, true);
                setPending(null);
              }}
            >
              Confirm
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPending(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {p.error && (
        <p role="alert" className="rounded bg-red-50 p-2 text-sm text-red-700" data-testid="launch-error">{p.error}</p>
      )}

      {count > 0 && (
        <div className="space-y-2 rounded border border-amber-200 bg-amber-50 p-3" data-testid="needs-review">
          <p className="text-sm font-medium text-amber-900">{from ? `Copied from ${from}: check before going live` : 'Check before going live'}</p>
          <ul className="space-y-1 text-sm text-amber-900">
            {launch.review.sections.map((s) => (
              <li key={s.key} className="flex flex-wrap items-center gap-2">
                <span>{s.label}</span>
                {p.canEdit && (
                  <Button size="sm" variant="outline" disabled={p.busy} onClick={() => p.onConfirmReviewed({ sections: [s.key] })}>
                    This is correct for {p.clubName}
                  </Button>
                )}
              </li>
            ))}
            {launch.review.promotions.map((o) => (
              <li key={o.id} className="flex flex-wrap items-center gap-2">
                <span>Offer “{o.title}”</span>
                {p.canEdit && (
                  <Button size="sm" variant="outline" disabled={p.busy} onClick={() => p.onConfirmReviewed({ promotionIds: [o.id] })}>
                    This is correct for {p.clubName}
                  </Button>
                )}
              </li>
            ))}
            {launch.review.followUps && (
              <li className="flex flex-wrap items-center gap-2">
                <span>Follow-ups</span>
                {p.canEdit && (
                  <Button size="sm" variant="outline" disabled={p.busy} onClick={() => p.onConfirmReviewed({ followUps: true })}>
                    This is correct for {p.clubName}
                  </Button>
                )}
              </li>
            )}
          </ul>
          <p className="text-xs text-amber-800">
            Saving a section after editing it also clears this. Copied prices are not quoted by the assistant until they are checked.
          </p>
        </div>
      )}
    </div>
  );
}
